<script setup>
import { computed } from 'vue';
import { feet_to_m } from '../calc.js';
import { useShared } from '../state.js';
import { usePanelRequests } from '../use_panels.js';

const { inputs, computedResult, isStale, recompute } = useShared();
const { requestPanel } = usePanelRequests();

const localComputed = computed(() => {
  let r = computedResult.value;
  return {
    calc_v: (r.v / feet_to_m).toFixed(2) + ' fps',
    calc_c: r.drag.toFixed(5),
    calc_fit: r.fit.toFixed(5),
  };
});

// calc_fit is the mean absolute residual across calibration marks, in the
// same "mark" units the user types into Measured Marks — so these
// thresholds are a fixed, unit-consistent read on fit quality regardless of
// how many marks were used. Not derived from real-world data, just a
// reasonable starting point: adjust if it doesn't match what a good vs.
// bad fit looks like in practice.
const FIT_GOOD_THRESHOLD = 0.05;
const FIT_OK_THRESHOLD = 0.15;

const fitQuality = computed(() => {
  const score = computedResult.value.fit;
  if (score < FIT_GOOD_THRESHOLD) return 'good';
  if (score < FIT_OK_THRESHOLD) return 'ok';
  return 'poor';
});

async function onRecompute() {
  await recompute();
  requestPanel('calculated');
}
</script>

<template>
  <div class="centered">
    <table>
      <tr>
        <td>Velocity:</td>
        <td class="right-align">
          <input class="input-result" type="text" readonly
            v-model.string="localComputed.calc_v" />
        </td>
      </tr>
      <tr>
        <td>Drag:</td>
        <td class="right-align">
          <input class="input-result" type="text" readonly
            v-model.number="localComputed.calc_c" />
        </td>
      </tr>
      <tr>
        <td>Fit Score (closer to 0 is better):</td>
        <td class="right-align">
          <input class="input-result fit-score" :class="fitQuality" type="text" readonly
            v-model.number="localComputed.calc_fit" />
        </td>
      </tr>
    </table>

    <p v-if="inputs.marks.length < 3" class="notice">
      Only {{ inputs.marks.length }} mark{{ inputs.marks.length === 1 ? '' : 's' }} — drag can't be
      calculated from fewer than 3, so a default estimate is being used instead. Add another mark
      for a proper fit.
    </p>

    <button
      @click="onRecompute()"
      :class="isStale ? 'btn-recompute' : 'btn-copy'"
      :title="isStale ? 'Inputs changed — recompute to refresh the results' : 'Recompute even if nothing has changed'"
    >
      {{ isStale ? 'Recalculate' : 'Force Recompute' }}
    </button>
  </div>
</template>

<style scoped>

.fit-score.good {
  background: color-mix(in srgb, var(--color-success) 25%, var(--input-bg));
  border-color: var(--color-success);
}

.fit-score.ok {
  background: color-mix(in srgb, var(--color-warning) 25%, var(--input-bg));
  border-color: var(--color-warning);
}

.fit-score.poor {
  background: color-mix(in srgb, var(--color-danger) 25%, var(--input-bg));
  border-color: var(--color-danger);
}

.notice {
  max-width: 40ch;
  margin: 0;
  padding: var(--space-sm) var(--space-md);
  background: color-mix(in srgb, var(--color-warning) 15%, var(--input-bg));
  border: var(--border-width) solid var(--color-warning);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  text-align: center;
}

</style>
